import type * as Highcharts from 'highcharts';
import type {} from 'highcharts/esm/modules/heatmap';

import type { HeatmapCell } from './room-utilization.helpers';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, hour) => {
  const period = hour < 12 ? 'AM' : 'PM';
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${twelveHour}${period}`;
});

const chartSpacing = [16, 24, 8, 24];

const NO_DATA_COLOR = '#E5E7EB';

export const ROOM_UTILIZATION_ESTIMATED_TOOLTIP = 'Estimated from Teams participant count. No headcount sensor in this room.';

/** The heatmap is built from Teams participant counts even when the room has a sensor. */
export const ROOM_UTILIZATION_HEATMAP_ESTIMATED_TOOLTIP = 'Estimated from Teams participant count.';

export interface RoomUtilizationHeatmapChart {
  maxValue: number;
  options: Highcharts.Options;
}

export function createRoomUtilizationHeatmapChart(cells: HeatmapCell[]) {
  const maxValue = Math.max(0, ...cells.filter(cell => cell.hasData).map(cell => cell.value));

  const data: Highcharts.PointOptionsObject[] = cells.map(cell => ({
    value: cell.hasData ? cell.value : null,
    x: cell.hour,
    y: cell.weekday,
  }));

  const options: Highcharts.Options = {
    chart: {
      height: 223,
      spacing: chartSpacing,
      type: 'heatmap',
      width: 600,
    },
    colorAxis: {
      max: maxValue || 1,
      min: 0,
      stops: [
        [0, '#F3F4F6'],
        [0.3, '#99F6E4'],
        [0.65, '#14B8A6'],
        [1, '#0F172A'],
      ],
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: { enabled: false },
    series: [
      {
        borderColor: '#FFFFFF',
        borderRadius: 5,
        borderWidth: 4,
        // Angular's <base href> turns the clip-path's url(#fragment) into a page re-fetch on each hover restyle; cells need no clipping.
        clip: false,
        data,
        nullColor: NO_DATA_COLOR,
        type: 'heatmap',
      },
    ],
    title: { text: undefined },
    tooltip: {
      formatter() {
        const weekday = WEEKDAY_LABELS[this.y as number];
        const hour = HOUR_LABELS[this.x as number];

        if (this.value === null || this.value === undefined) {
          return `${weekday}, ${hour} – no data`;
        }

        return `${weekday}, ${hour} – ${this.value.toFixed(1)} ppl/hr`;
      },
      // Same <base href> + url(#fragment) issue as `clip` above - the default drop-shadow is an SVG filter, re-resolved on every hover.
      shadow: false,
    },
    xAxis: {
      categories: HOUR_LABELS,
      gridLineWidth: 0,
      lineWidth: 0,
      tickInterval: 6,
      tickWidth: 0,
    },
    yAxis: {
      categories: WEEKDAY_LABELS,
      gridLineWidth: 0,
      lineWidth: 0,
      // Heatmap y-axis defaults category 0 to the bottom - reversed so Monday renders on top.
      reversed: true,
      tickWidth: 0,
      title: { text: undefined },
    },
  };

  return { maxValue, options };
}

export interface RoomUtilizationBarSeries {
  color?: string;
  data: (number | null)[];
  name: string;
}

export function createRoomUtilizationBarChart(
  categories: string[],
  series: RoomUtilizationBarSeries[],
  yAxisTitle: string,
  xAxisTitle?: string,
  showLegend = true,
) {
  return {
    chart: {
      spacing: chartSpacing,
      type: 'column',
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: { enabled: showLegend },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          formatter(this: Highcharts.Point) {
            return this.y ? this.y : '';
          },
        },
      },
    },
    series: series.map(s => ({
      color: s.color,
      data: s.data,
      name: s.name,
      type: 'column',
    })),
    title: { text: undefined },
    xAxis: {
      categories,
      title: { text: xAxisTitle },
    },
    yAxis: {
      allowDecimals: false,
      min: 0,
      // With all-zero data min and max collapse to 0 and Highcharts pads the range both ways, floating the 0 line above the X axis.
      softMax: 1,
      title: { text: yAxisTitle },
    },
  };
}
